import React, { useState } from 'react';

const DocumentViewerModal = ({ isOpen, onClose, documentUrl, documentName }) => {
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  if (!isOpen) return null;

  // Determine file type based on extension
  const getFileType = (name) => {
    if (!name) return 'unknown';
    const extension = name.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    return 'other';
  };

  const fileType = getFileType(documentName);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-4 text-center sm:p-0">
        
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-90 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal content */}
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full h-[90vh] flex flex-col max-w-6xl">
          
          {/* Header */}
          <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h3 className="text-lg leading-6 font-medium text-gray-900 truncate pr-4" id="modal-title">
              {documentName || 'Document Viewer'}
            </h3>
            
            <div className="flex items-center gap-2">
              {fileType === 'image' && (
                <div className="flex items-center bg-gray-100 rounded-lg p-1 mr-4">
                  <button onClick={handleZoomOut} className="p-1 hover:bg-gray-200 rounded text-gray-600" title="Zoom Out">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                  </button>
                  <span className="text-xs font-medium w-12 text-center text-gray-600">{zoom}%</span>
                  <button onClick={handleZoomIn} className="p-1 hover:bg-gray-200 rounded text-gray-600" title="Zoom In">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  </button>
                  <div className="w-px h-4 bg-gray-300 mx-2"></div>
                  <button onClick={handleRotate} className="p-1 hover:bg-gray-200 rounded text-gray-600" title="Rotate">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  </button>
                  <button onClick={handleReset} className="p-1 hover:bg-gray-200 rounded text-gray-600" title="Reset">
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}
              
              <button
                type="button"
                className="bg-gray-100 rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-200 focus:outline-none"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 bg-gray-100 relative overflow-hidden flex items-center justify-center p-4">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
              </div>
            )}
            
            {documentUrl ? (
              fileType === 'image' ? (
                <div className="overflow-auto w-full h-full flex items-center justify-center">
                   <img
                    src={documentUrl}
                    alt={documentName}
                    className="transition-transform duration-200 ease-out origin-center object-contain"
                    style={{ 
                      transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                      maxHeight: '100%',
                      maxWidth: '100%'
                    }}
                    onLoad={() => setLoading(false)}
                    onError={() => setLoading(false)}
                  />
                </div>
              ) : (
                <iframe
                  src={documentUrl}
                  className="w-full h-full border-0 bg-white shadow-sm"
                  title="Document Preview"
                  onLoad={() => setLoading(false)}
                />
              )
            ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                    Failed to load document URL.
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;
